/**
 * Barrel-free helpers for the customer ONBOARDING view (ADO #1997). Kept in their OWN module (no
 * `continity-shared-ui` import) so they are unit-testable under Vitest without tripping the shared-ui
 * barrel resolution (same reason as `agents/agent-status.util.ts`).
 *
 * Type-only imports (erased at compile time) keep this module barrel-free + Vitest-safe.
 */
import type {
  CustomerOnboardingActionKind,
  CustomerOnboardingState,
  PhoenixCustomerOnboardingPhase,
} from '../shared/api/dtos/phoenix-customer-onboarding.dtos';

/**
 * Customer-onboarding read endpoint path, appended to `ShellConfigService.apiUrl`.
 * `GET api/phoenix/onboarding` → `PhoenixCustomerOnboardingController`. The tenant is server-pinned from
 * the CompanyId claim — nothing tenant-derivable is sent.
 */
export const ONBOARDING_ENDPOINT_PATH = '/phoenix/onboarding';
export const SITES_ENDPOINT_PATH = '/phoenix/sites';
export const SITE_ORDER_OPTIONS_ENDPOINT_PATH = '/phoenix/sites/options';

/** Absolute customer-onboarding endpoint from the shell's resolved API base. */
export function onboardingStatusUrl(apiUrl: string): string {
  return `${apiUrl}${ONBOARDING_ENDPOINT_PATH}`;
}

/** Absolute tenant-facing first-site reservation endpoint. */
export function sitesUrl(apiUrl: string): string {
  return `${apiUrl}${SITES_ENDPOINT_PATH}`;
}

/** Server-curated choices for the site order. They intentionally have no client default. */
export function siteOrderOptionsUrl(apiUrl: string): string {
  return `${apiUrl}${SITE_ORDER_OPTIONS_ENDPOINT_PATH}`;
}

/** The pre-onboarding order card is shown only until the first PhoenixSite exists. */
export function onboardingNeedsSiteOrder(siteCount: number): boolean {
  return siteCount === 0;
}

export type SiteOrderErrorKind = 'error' | 'domainError' | 'paymentDeclined' | 'skuUnavailable';

/**
 * Customer-safe mapping of the actionable order outcomes (AB#2107): the fixable domain
 * precondition, the declined payment (nothing was booked, a fresh order is always possible) and the
 * fail-closed "no SKU covers this size" refusal; all other failures stay generic.
 */
export function siteOrderErrorKind(errorCode: unknown): SiteOrderErrorKind {
  switch (errorCode) {
    case 'COMPANY_DOMAIN_REQUIRED':
      return 'domainError';
    case 'PAYMENT_FAILED':
      return 'paymentDeclined';
    case 'SKU_UNAVAILABLE':
      return 'skuUnavailable';
    default:
      return 'error';
  }
}

// ── SITE-ADDRESSED SETUP ROUTE (AB#2274) ─────────────────────────────────────────────────────────
//
// The setup page renders ONE journey per Phoenix site. With a second site a global setup page becomes
// contradictory ("the setup starts over"), so the route itself is addressable: `/onboarding/{siteId}`
// focuses that site's journey and is the deep-link target of the per-environment setup links. The
// decision is deliberately a ROUTE, not a dialog (2026-07-24): journey actions redirect (installer,
// consent) and only survive on a stable URL, and a second surface would duplicate the same content.

/** Route param carrying the addressed Phoenix site — see `app.routes.ts` and `APP_ROUTES.onboarding`. */
export const ONBOARDING_SITE_ROUTE_PARAM = 'phoenixSiteId';

/** What the setup page renders for a given route address. */
export interface OnboardingSiteAddressing<T> {
  /** The journeys to render: the addressed one alone, or all of them. */
  visible: T[];
  /** The addressed journey when the address resolved; null on the plain overview. */
  focused: T | null;
  /**
   * True when an address was given that does NOT belong to this tenant (or no longer exists). The page
   * falls back to the overview and SAYS so — silently showing everything would leave the customer
   * believing they are looking at the site they addressed.
   */
  unresolvedAddress: boolean;
}

/**
 * Resolves the route-addressed site against the tenant's own journeys. Pure: the page renders the
 * result and decides nothing itself.
 *
 * The comparison is trimmed + case-insensitive because a GUID in a URL is not case-normalised (a link
 * pasted from a log or an upper-cased id must still resolve to the same site). An unknown or foreign id
 * can only ever fall back — the response contains this tenant's sites only, so a foreign id is
 * indistinguishable from a stale one here, and both deserve the same honest fallback.
 */
export function resolveAddressedSite<T extends { phoenixSiteId: string }>(
  sites: readonly T[],
  addressedSiteId: string | null | undefined,
): OnboardingSiteAddressing<T> {
  const all = [...sites];
  const address = addressedSiteId?.trim() ?? '';
  if (!address) return { visible: all, focused: null, unresolvedAddress: false };

  const focused = all.find((s) => s.phoenixSiteId.toLowerCase() === address.toLowerCase()) ?? null;
  return focused
    ? { visible: [focused], focused, unresolvedAddress: false }
    : { visible: all, focused: null, unresolvedAddress: true };
}

// NOTE: `resolveEnvironmentTargetSite` (AB#2506) was removed together with the "Add environment"
// wizard (Kevin 2026-08-06): the tenant FE no longer creates environments, so nothing client-side
// has to resolve a birth site any more. A future site-addressed create entry (AB#2274/AB#2275)
// carries the site id from its own context instead of re-resolving it globally.

// ── FE-LOCAL JOURNEY LAYER (AB#2513) ─────────────────────────────────────────────────────────────
//
// The backend returns the FLAT per-phase list (a faithful, customer-safe mirror of the admin composite:
// tenant-wide phases once + every site's own phases). Turning that into a small customer JOURNEY is a
// pure DISPLAY concern and lives HERE (FE-only), mirroring how the admin two-level grouping is FE-only
// (AB#2019). The 15 canonical phases, their order, keys, kinds and states are NOT touched.
//
// AB#2513 fixed the three contradictions the old 5-group cut produced on one screen:
//   1. TWO progress notions — a headline that counted PHASES ("13 of 15") above a list that rendered
//      GROUPS. There is now exactly ONE: the counter names the step the list is at ("step 2 of 4").
//   2. A DUPLICATE first step — the tenant-level phases were rendered in their own card ABOVE the site
//      journey (and, worse, the tenant card's group listed `ordering`, a REALM-level key it could never
//      resolve, while the four tenant-level readiness phases sat in a SITE group that could not resolve
//      them either — so `adminConsent`/`deploySpProvision`/`p1License`/`resourceProviders` rendered
//      NOWHERE while still counting in the denominator). The tenant phases are now merged INTO each
//      journey in canonical order, where they are genuinely that journey's prerequisites.
//   3. A THIRD rendering of the same state — the "the team is setting up" banner. Gone: that IS the
//      state of the Continity step.
//
// The cut is CONTIGUOUS in the canonical order and names the PRIMARY responsibility of each high-level step.
// A current internal phase may temporarily hand ownership to the Continity Phoenix Team (HIA is the concrete
// case); buildOnboardingJourney derives the displayed owner from that current phase so the badge never tells the
// customer to act while the team is working. Contiguity is not cosmetic: "step N of 4" is only true if the
// steps complete in order, so a step may never bundle a phase behind a LATER step's phases.

/** Who currently has to act on a journey step. `continity` = the Continity Phoenix Team (the customer waits);
 *  `customer` = the tenant's own side of the setup. */
export type CustomerOnboardingStepOwner = 'customer' | 'continity';

/** One step of the customer journey — a contiguous slice of the canonical phase order plus its default owner. */
export interface CustomerOnboardingStepDef {
  key: string;
  /** Default/high-level owner; the current phase's audience may temporarily override it in the rendered view. */
  owner: CustomerOnboardingStepOwner;
  /** The canonical phase keys this step bundles — a contiguous slice of `PhoenixOnboardingPhaseKeys.OrderedAll`. */
  phaseKeys: readonly string[];
}

/**
 * The FOUR customer journey steps, top to bottom. Their union is exactly the canonical flat order
 * (`PhoenixOnboardingPhaseKeys.OrderedAll`), each key exactly once — the `onboarding.util.spec`
 * invariant that keeps a newly projected backend key from being silently dropped (and the counter from
 * drifting away from what is rendered).
 *
 * Assignment evidence (who actually MOVES the phase, read off the backend, not guessed):
 *   • `ordering`             → step 1. The site's existence IS the order and the CUSTOMER places it
 *     (tenant-facing `POST api/phoenix/sites`, the order card on this very page); the phase is auto-Done
 *     from that moment (`BuildOrderingPhaseForRealm`). By the time this step RENDERS as a journey the
 *     order is therefore already behind the customer ("Bestellung erhalten").
 *   • `customerAzTenant`     → step 1. The customer's Azure coordinates; the team records them
 *     (`NextStepHint`: "create/complete the customer Az tenant record") from what the customer supplies.
 *   • `adminConsent`         → step 1. Admin consent granted IN THE CUSTOMER'S Azure tenant
 *     (`NextStepHint`: "a Global Admin of the customer tenant grants admin consent …"). Executed with the
 *     team's 2FA/JIT access, so the portal offers the customer no button — it stays a wait-state here.
 *
 * OWNER of step 1 = `continity` (Kevin 2026-08-06, E2E finding): the step rendered with the customer
 * badge although none of its three phases has anything left for the customer to do — the order is
 * already placed when the journey exists, and connection + consent are Phoenix-Team work (the phases'
 * own `audience`/`actionKind` never prompt the customer here, see the fresh-journey spec). The badge
 * now honestly says the Continity Phoenix Team is at work. The customer's OWN consent (EXO, #2031) is
 * NOT part of this step.
 *   • `deploySpProvision`, `p1License`, `resourceProviders`, `quota`, `exportEnvRollout`,
 *     `lighthouseDelegation` → step 2. All six are "Continity Phoenix Team" hints in `PhoenixOnboardingFold`
 *     (deploy-SP provisioning, P1 licence, resource providers, quota, the deploy itself — and the
 *     Lighthouse delegation, which the export-env DEPLOY establishes; `BuildLighthouseDelegationPhase`
 *     probes it, no customer acceptance step exists in the product).
 *   • `agentInstallEnroll`   → step 3. The one genuine in-portal customer action ("download and install
 *     the Phoenix agent, then complete enrollment").
 *   • `certRegistration`     → step 3. The ENROLLED AGENT auto-registers its own certificate — the reason
 *     the canonical order puts it directly behind enrollment. Automatic, no prompt, but it belongs to the
 *     customer's install block rather than to a Continity step.
 *   • `gaServiceUser`        → step 3. Team-provisioned HIA identity after agent/certificate readiness; it is
 *     config-independent and therefore blocks neither on config authoring nor on its validation result. While
 *     it is open, the customer-facing step remains a wait-state without a customer CTA.
 *   • `configValidation`     → step 3. The second in-portal customer action (complete sources/credentials,
 *     run the non-destructive test), offered only after HIA setup is ready.
 *   • `exportRestoreVerify`, `goLive` → step 4. The export/restore proof and the go-live sign-off.
 *
 * DEVIATION from the approved concept sketch, deliberate: the sketch listed "Zertifikat, Service-User"
 * under step 2. Both sit BEHIND the customer's install step in the canonical order (the cert is registered
 * by the enrolled agent; the HIA service-user pair is reported by that same agent and is consumed by the
 * restore proof), so folding them into step 2 would make step 2 finish after step 3 — and "step 2 of 4"
 * would stop being true. They are placed where the dependency order puts them instead.
 */
export const CUSTOMER_ONBOARDING_STEPS: readonly CustomerOnboardingStepDef[] = [
  {
    key: 'accessSetup',
    owner: 'continity',
    phaseKeys: ['ordering', 'customerAzTenant', 'adminConsent'],
  },
  {
    key: 'environmentSetup',
    owner: 'continity',
    phaseKeys: [
      'deploySpProvision',
      'p1License',
      'resourceProviders',
      'quota',
      'exportEnvRollout',
      'lighthouseDelegation',
    ],
  },
  {
    key: 'installAgent',
    owner: 'customer',
    phaseKeys: ['agentInstallEnroll', 'certRegistration', 'gaServiceUser', 'configValidation'],
  },
  {
    key: 'verification',
    owner: 'continity',
    phaseKeys: ['exportRestoreVerify', 'goLive'],
  },
];

// NOT BUILT, deliberately (AB#2513, follow-up needed before it can be): the concept also asks that an
// internal phase which is BLOCKED and which ONLY the customer can resolve is pulled up as an action prompt
// (and unfolds its step by itself). Two things are missing for that, and neither may be invented here:
//   1. no signal — the customer-safe projection collapses every internal Pending/Error/Unknown into
//      `InProgress` by design (the information boundary in `PhoenixCustomerOnboardingStates`), so a blocked
//      internal phase is indistinguishable from a running one on this wire;
//   2. no such phase today — `PhoenixOnboardingFold.NextStepHint` attributes p1License, quota AND the
//      Lighthouse delegation to the Continity Phoenix Team (the delegation is established by the export-env
//      deploy, not accepted by the customer), so there is currently nothing to escalate.
// Guessing one would produce exactly the nagging this rework removed. Until the backend can say "blocked,
// and the customer is the only one who can clear it", the step stays a wait-state and the team reaches out
// (which its copy promises).

/** One line of a step's detail list — the internal phases are SUMMARISED into a step, never hidden. */
export interface CustomerOnboardingStepDetail {
  /** The canonical phase key (drives the localized label). */
  key: string;
  state: CustomerOnboardingState;
}

/** A computed step row for the stepper. */
export interface CustomerOnboardingStepView {
  key: string;
  owner: CustomerOnboardingStepOwner;
  /** 1-based position — the number the headline counter names ("step {position} of {total}"). */
  position: number;
  state: CustomerOnboardingState;
  /** True for the one step the journey is currently at (the first step that is not Done). */
  isCurrent: boolean;
  /**
   * The prompt this step offers RIGHT NOW — non-null ONLY when the customer can actually act on it
   * (`state === 'ActionRequired'`). A step nobody outside Continity can move never carries one, which is
   * the whole "no action prompt where the customer cannot act" rule, enforced in one place.
   */
  actionKind: CustomerOnboardingActionKind | null;
  /** The step's phases, in canonical order — each rendered with its own state dot. */
  details: CustomerOnboardingStepDetail[];
}

/** The whole customer journey for ONE site: the four steps plus the single progress notion above them. */
export interface CustomerOnboardingJourney {
  steps: CustomerOnboardingStepView[];
  totalSteps: number;
  /** 1-based position of the step the journey is at; equals {@link totalSteps} once complete. */
  currentStep: number;
  /** The current step's key (the headline names it, so counter and list can never disagree). */
  currentStepKey: string;
  complete: boolean;
  /** True while any step waits on the customer — derived from the steps, never from a separate flag. */
  needsCustomerAction: boolean;
}

/**
 * Builds ONE journey from a flat customer phase list.
 *
 * The caller passes the tenant-wide phases AND one site's phases together (order irrelevant — the step
 * definitions carry the canonical order); the tenant-wide prerequisites are genuinely part of every
 * site's journey, and merging them is what removes the second, contradicting progress notion.
 *
 * State rules (the four states of the concept: open / running / blocked / done):
 *   • every phase of the step Done                        ⇒ `Done`;
 *   • not the current step                                ⇒ `Upcoming` ("open", not reached yet);
 *   • current step, first not-done phase is a CUSTOMER action ⇒ `ActionRequired` ("blocked — your turn"),
 *     unless that action is itself already running (a queued/running configuration test) ⇒ `InProgress`;
 *   • current step, first not-done phase is internal/automatic ⇒ `InProgress` ("running").
 * A running provisioning is therefore never "blocked", and a step whose next open phase is internal never
 * shows a prompt — the customer cannot move it.
 *
 * Taking the FIRST not-done phase in canonical order (instead of "any customer phase in the step") also
 * keeps the prompt honest inside a step: while the agent is still auto-registering its certificate, the
 * step waits rather than telling the customer to open a configuration that cannot pass yet.
 *
 * A step whose phases the response does not contain at all stays `Upcoming` and blocks completion — a
 * missing signal must never read as a fabricated green.
 */
export function buildOnboardingJourney(
  phases: readonly PhoenixCustomerOnboardingPhase[],
  stepDefinitions: readonly CustomerOnboardingStepDef[] = CUSTOMER_ONBOARDING_STEPS,
): CustomerOnboardingJourney {
  const byKey = new Map(phases.map((p) => [p.key, p]));
  const resolved = stepDefinitions.map((def) => {
    const subs = def.phaseKeys
      .map((k) => byKey.get(k))
      .filter((p): p is PhoenixCustomerOnboardingPhase => !!p);
    return { def, subs, firstOpen: subs.find((p) => p.state !== 'Done') };
  });

  const currentIndex = resolved.findIndex((s) => s.subs.length === 0 || !!s.firstOpen);
  const complete = stepDefinitions.length > 0 && currentIndex < 0;

  const steps = resolved.map((s, index): CustomerOnboardingStepView => {
    const isCurrent = index === currentIndex;
    const state = stepState(s.subs.length, s.firstOpen, isCurrent);
    return {
      key: s.def.key,
      owner: isCurrent && s.firstOpen
        ? currentPhaseOwner(s.firstOpen)
        : s.def.owner,
      position: index + 1,
      state,
      isCurrent,
      actionKind: state === 'ActionRequired' ? s.firstOpen?.actionKind ?? null : null,
      details: s.subs.map((p) => ({ key: p.key, state: p.state })),
    };
  });

  return {
    steps,
    totalSteps: steps.length,
    currentStep: complete ? steps.length : currentIndex + 1,
    currentStepKey: (complete ? steps.at(-1) : steps[currentIndex])?.key ?? '',
    complete,
    needsCustomerAction: steps.some((s) => s.state === 'ActionRequired'),
  };
}

/** Display ownership follows the current actionable boundary: customer phases stay customer-owned; automated
 * and team phases are shown as Continity work so an internal HIA wait never carries a customer badge. */
function currentPhaseOwner(phase: PhoenixCustomerOnboardingPhase): CustomerOnboardingStepOwner {
  return phase.audience === 'Customer' ? 'customer' : 'continity';
}

function stepState(
  subCount: number,
  firstOpen: PhoenixCustomerOnboardingPhase | undefined,
  isCurrent: boolean,
): CustomerOnboardingState {
  if (subCount === 0) return 'Upcoming';
  if (!firstOpen) return 'Done';
  if (!isCurrent) return 'Upcoming';
  if (firstOpen.actionKind) {
    return firstOpen.state === 'InProgress' ? 'InProgress' : 'ActionRequired';
  }
  return 'InProgress';
}
